/**
 * «من المريض» — what a linked patient wrote about themselves, offered to the
 * institution's file (المرحلة 1.2, T7).
 *
 * When the file is linked to the patient's account, the desk sees what the
 * account says and the file does not: allergies, chronic conditions, current
 * medicines, and the insurance card they photographed. The desk ticks what it
 * judges right and takes it in; taking it adds items and fills blanks, and
 * never replaces what the institution wrote (lib/healthProfileLink.js). An
 * unlinked file, or a linked one with nothing new, shows nothing here.
 *
 * Below it, the desk records an allergy of its own on the file.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Field, Icon, Input, Panel, Select } from '../../components/common';
import { patientHealthApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { CardStatus } from '../patient/InsuranceCardCapture';

const SEVERITIES = ['mild', 'moderate', 'severe'];

export function FromPatientPanel({ fileId, canWrite = false, onTaken }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [offer, setOffer] = useState(null);
  const [allergyIds, setAllergyIds] = useState([]);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    patientHealthApi
      .fromPatient(fileId)
      .then((answer) => {
        setOffer(answer);
        // Everything ticked to start with: the desk unticks what it does not take.
        setAllergyIds((answer.allergies || []).map((a) => a.id));
        setFields(['chronicConditions', 'currentMedications', 'insuranceCard'].filter((f) => (f === 'insuranceCard' ? answer.insuranceCard : (answer[f] || []).length)));
      })
      .catch(() => setOffer(null));
  }, [fileId]);
  useEffect(() => {
    load();
  }, [load]);

  if (!offer || !offer.linked) return null;
  const empty = !offer.allergies.length && !offer.chronicConditions.length && !offer.currentMedications.length && !offer.insuranceCard;
  if (empty) return null;

  const toggle = (list, set, value) => set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const take = async () => {
    setBusy(true);
    try {
      const answer = await patientHealthApi.accept(fileId, { allergyIds, fields });
      success(t('fromPatient.taken', { n: answer.taken.length }));
      setOffer(answer.offer);
      onTaken?.(answer);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel className="from-patient" data-testid="from-patient">
      <header className="from-patient__head">
        <span className="from-patient__badge">
          <Icon name="user-round" size={14} /> {t('allergy.fromPatient')}
        </span>
        <div>
          <h3>{t('fromPatient.title', { name: offer.name || '' })}</h3>
          <p>{t('fromPatient.intro')}</p>
        </div>
      </header>

      {offer.allergies.length > 0 && (
        <section className="from-patient__group">
          <h4>{t('fromPatient.allergies')}</h4>
          {offer.allergies.map((a) => (
            <Checkbox
              key={a.id}
              checked={allergyIds.includes(a.id)}
              disabled={!canWrite}
              onChange={() => toggle(allergyIds, setAllergyIds, a.id)}
              label={
                <span className={`from-patient__allergy from-patient__allergy--${a.severity}`}>
                  <strong>{a.substance}</strong>
                  {a.reaction ? ` — ${a.reaction}` : ''} · {t(`healthProfile.allergies.severity.${a.severity}`)}
                </span>
              }
            />
          ))}
        </section>
      )}

      {offer.chronicConditions.length > 0 && (
        <section className="from-patient__group">
          <Checkbox checked={fields.includes('chronicConditions')} disabled={!canWrite} onChange={() => toggle(fields, setFields, 'chronicConditions')} label={<strong>{t('fromPatient.conditions')}</strong>} />
          <p className="from-patient__list">{offer.chronicConditions.join(' · ')}</p>
        </section>
      )}

      {offer.currentMedications.length > 0 && (
        <section className="from-patient__group">
          <Checkbox checked={fields.includes('currentMedications')} disabled={!canWrite} onChange={() => toggle(fields, setFields, 'currentMedications')} label={<strong>{t('fromPatient.medicines')}</strong>} />
          <p className="from-patient__list">{offer.currentMedications.map((m) => [m.name, m.dose, m.frequency].filter(Boolean).join(' ')).join(' · ')}</p>
        </section>
      )}

      {offer.insuranceCard && (
        <section className="from-patient__group">
          <Checkbox checked={fields.includes('insuranceCard')} disabled={!canWrite} onChange={() => toggle(fields, setFields, 'insuranceCard')} label={<strong>{t('fromPatient.card')}</strong>} />
          <div className="from-patient__card">
            <img src={offer.insuranceCard.image} alt={t('healthProfile.card.photoAlt')} data-testid="from-patient-card" />
            <dl>
              {offer.insuranceCard.provider && (
                <>
                  <dt>{t('healthProfile.card.provider')}</dt>
                  <dd>{offer.insuranceCard.provider}</dd>
                </>
              )}
              {offer.insuranceCard.policyNo && (
                <>
                  <dt>{t('healthProfile.card.policyNo')}</dt>
                  <dd dir="ltr">{offer.insuranceCard.policyNo}</dd>
                </>
              )}
              {offer.insuranceCard.expiry && (
                <>
                  <dt>{t('healthProfile.card.expiry')}</dt>
                  <dd dir="ltr">{offer.insuranceCard.expiry}</dd>
                </>
              )}
            </dl>
            <CardStatus status={offer.insuranceCard.status} />
          </div>
        </section>
      )}

      {canWrite && (
        <div className="hp-actions">
          <Button icon="check" onClick={take} loading={busy} disabled={!allergyIds.length && !fields.length} data-testid="from-patient-take">
            {t('fromPatient.take')}
          </Button>
          <p className="hp-note">{t('fromPatient.never')}</p>
        </div>
      )}
    </Panel>
  );
}

/** The desk records an allergy on the file itself (verified, the institution's). */
export function FileAllergyForm({ fileId, onAdded }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ substance: '', reaction: '', severity: 'moderate' });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      await patientHealthApi.addAllergy(fileId, form);
      success(t('allergy.added'));
      setForm({ substance: '', reaction: '', severity: 'moderate' });
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button variant="ghost" icon="shield-alert" onClick={() => setOpen(true)} data-testid="file-allergy-open">
        {t('allergy.addToFile')}
      </Button>
    );
  }
  return (
    <div className="file-allergy-form">
      <div className="hp-grid">
        <Field label={t('healthProfile.allergies.substance')}>
          <Input value={form.substance} onChange={(e) => setForm({ ...form, substance: e.target.value })} data-testid="file-allergy-substance" />
        </Field>
        <Field label={t('healthProfile.allergies.reaction')}>
          <Input value={form.reaction} onChange={(e) => setForm({ ...form, reaction: e.target.value })} />
        </Field>
        <Field label={t('healthProfile.allergies.severityLabel')}>
          <Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {t(`healthProfile.allergies.severity.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="hp-actions">
        <Button icon="check" onClick={add} loading={busy} disabled={!form.substance.trim()} data-testid="file-allergy-save">
          {t('allergy.save')}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

export default FromPatientPanel;
